import { skipToken } from '@reduxjs/toolkit/query/react'
import { useFetchUserStatusQuery } from '@/api/user-api'
import { useFetchKolPerformanceSummaryQuery } from '@/api/workspace-api/kol-api'
import useKolDetailRoute from '@/components/kol/hooks/use-kol-detail-route'
import usePublicKolInfoContext from '@/components/kol/hooks/use-public-kol-info-context'
import routes from '@/hooks/use-authorization/routes'
import { usePermissions } from '@/hooks/use-permissions'
import { type KolMetricPerformanceSummary } from '@/types/schema/kol-schema'

type UseKolMetricPerformanceSummary = (
  queryTrigger?: typeof skipToken | undefined,
) => {
  performanceSummary?: KolMetricPerformanceSummary
  isFetchingPerformanceSummary: boolean
  isErrorPerformanceError: boolean
}

const useKolMetricPerformanceSummary: UseKolMetricPerformanceSummary = (
  queryTrigger,
) => {
  const params = routes.kolId.useParams()
  const publicKolInfoContext = usePublicKolInfoContext()
  const { withKolPermission } = usePermissions()
  const { searchParams } = useKolDetailRoute()

  const shouldFetchUserStatus =
    queryTrigger === undefined &&
    !withKolPermission &&
    !publicKolInfoContext?.publicKolInfo

  const {
    data: userStatus,
    isFetching: isFetchingUserStatus,
    isError: isErrorToFetchUserStatus,
  } = useFetchUserStatusQuery(shouldFetchUserStatus ? undefined : skipToken)

  const {
    data: metricPerformanceData,
    isFetching: isFetchingMetricPerformanceData,
    isError: isErrorToFetchMetricPerformanceData,
  } = useFetchKolPerformanceSummaryQuery(
    !withKolPermission &&
      userStatus?.currentWorkspaceId &&
      params.data?.kolId &&
      searchParams.data?.platform &&
      searchParams.data?.duration &&
      !publicKolInfoContext?.publicKolInfo
      ? {
          radar_uuid: params.data.kolId,
          platform_type: searchParams.data.platform,
          scope: searchParams.data.duration,
        }
      : skipToken,
  )

  return {
    performanceSummary: metricPerformanceData,
    isFetchingPerformanceSummary:
      isFetchingUserStatus || isFetchingMetricPerformanceData,
    isErrorPerformanceError:
      isErrorToFetchUserStatus || isErrorToFetchMetricPerformanceData,
  }
}

export default useKolMetricPerformanceSummary
